import { json } from "react-router-dom";
import * as apiClient from "../apiClient";
import App from "../App";
import AppError from "../AppError";
import AudienceBulkDeleteDialog, {
  action as deleteAudiences,
} from "../features/audiences/AudienceBulkDeleteDialog";
import { loader as loadPodcastCampaignSettings } from "../features/managed_plus_podcasts/SettingsTab";
import AccountTimeOnSiteForm, {
  action as managedplusAudienceFloorAction,
} from "../features/managed_plus_pricing/AccountTimeOnSiteForm.js";
import EditBidder, {
  action as updateBidder,
  loader as bidderLoader,
} from "../features/programmatic_supply/EditBidder";
import NewBidder, {
  action as createBidder,
} from "../features/programmatic_supply/NewBidder";
import SegmentDeactivationDialog, {
  action as deactivateSegment,
} from "../features/segments/components/SegmentDeactivationDialog";
import SegmentTargetingJobDialog, {
  action as createSegmentTargetingJob,
} from "../features/segments/components/SegmentTargetingJobDialog";
import Advertisers, { loader as advertisersLoader } from "../pages/Advertisers";
import Audience, {
  action as updateAudience,
  loader as loadAudience,
} from "../pages/Audience";
import Audiences, { loader as loadAudiences } from "../pages/Audiences";
import BulkEditManagedPlusContact, {
  action as contactBulkEditAction,
} from "../pages/BulkEditManagedPlusContact";
import BulkEditManagedPlusCredit, {
  action as creditBulkEditAction,
} from "../pages/BulkEditManagedPlusCredit";
import EditAdvertiser, {
  action as updateAdvertiser,
  loader as loadAdvertiser,
} from "../pages/EditAdvertiser";
import EditManagedPlusContact, {
  action as updateContactAction,
  loader as managedPlusContactLoader,
} from "../pages/EditManagedPlusContact";
import EditManagedPlusCredit, {
  action as updateCreditAction,
  loader as ManagedPlusCreditLoader,
} from "../pages/EditManagedPlusCredit";
import Error from "../pages/Error";
import GlobalAudienceFloors from "../pages/GlobalAudienceFloors";
import ManagedPlusCredits from "../pages/ManagedPlusCredits";
import ManagedPlusCustomAudienceFloors from "../pages/ManagedPlusCustomAudienceFloors.js";
import ManagedPlusPodcastCampaign from "../pages/ManagedPlusPodcastCampaign.js";
import ManagedPlusPodcastCampaigns from "../pages/ManagedPlusPodcastCampaigns.js";
import ManagedPlusSoxEmails from "../pages/ManagedPlusSoxEmails";
import NewAdvertiser, {
  action as createAdvertiser,
} from "../pages/NewAdvertiser";
import NewAudience, { action as createNewAudience } from "../pages/NewAudience";
import NewManagedPlusContact, {
  action as createContactAction,
} from "../pages/NewManagedPlusContact";
import NewManagedPlusCredit, {
  action as NewCreditAction,
} from "../pages/NewManagedPlusCredit";
import NewRetargetingPixel, {
  action as createNewRetargetingPixel,
} from "../pages/NewRetargetingPixel";
import NewSegment, { action as createNewSegment } from "../pages/NewSegment";
import PlacementPrebidConfigurations from "../pages/PlacementPrebidConfigurations";
import ProgrammaticSupplyBidders, {
  loader as programmaticSupplyBiddersLoader,
} from "../pages/ProgrammaticSupplyBidders";
import ProgrammaticSupplyGlobalSettings, {
  action as updateProgrammaticSupplySettings,
  loader as programmaticSupplySettingsLoader,
} from "../pages/ProgrammaticSupplyGlobalSettings";
import ProgrammaticSupplyPublisher, {
  action as updateProgrammaticSupplyPublisher,
  loader as programmaticSupplyPublisherLoader,
} from "../pages/ProgrammaticSupplyPublisher";
import ProgrammaticSupplyPublishers, {
  loader as programmaticSupplyPublishersLoader,
} from "../pages/ProgrammaticSupplyPublishers";
import Publishers from "../pages/Publishers";
import RetargetingPixel, {
  action as updateRetargetingPixel,
  loader as loadRetargetingPixel,
} from "../pages/RetargetingPixel";
import RetargetingPixels, {
  loader as loadPixels,
} from "../pages/RetargetingPixels";
import Segment, {
  action as updateSegment,
  loader as loadSegment,
} from "../pages/Segment";
import Segments, { loader as loadSegments } from "../pages/Segments";
import TargetingJobs, {
  loader as loadTargetingJobs,
} from "../pages/TargetingJobs";
import { featureFlagFetcher as flagLoader } from "./featureFlags.js";

const routes = [
  {
    path: "/",
    element: <App />,
    id: "featureFlagLoader",
    loader: flagLoader,
    errorElement: <AppError />,
    children: [
      {
        id: "advertisers",
        path: "advertisers",
        children: [
          {
            loader: advertisersLoader,
            element: <Advertisers />,
            index: true,
          },
          {
            id: "advertiserCategories",
            loader: async ({ request }) => {
              const { data: advertiserCategories } = await apiClient.get(
                "player",
                "/api/v1/advertiser_categories",
                {
                  signal: request.signal,
                }
              );

              return advertiserCategories;
            },
            children: [
              {
                action: createAdvertiser,
                path: "new",
                element: <NewAdvertiser />,
              },
              {
                action: updateAdvertiser,
                loader: loadAdvertiser,
                path: ":id/edit",
                element: <EditAdvertiser />,
                id: "advertiser",
              },
            ],
          },
        ],
      },
      {
        errorElement: <Error />,
        id: "segmentTypes",
        loader: async ({ request }) => {
          const { result: segmentTypes } = await apiClient.get(
            "audience",
            "/v1/segment_types",
            {
              signal: request.signal,
            }
          );

          return segmentTypes;
        },
        shouldRevalidate: () => false,
        children: [
          {
            element: <Segments />,
            index: true,
            loader: loadSegments,
          },
          {
            action: createNewSegment,
            element: <NewSegment />,
            path: "segments/new",
          },
          {
            id: "segment",
            loader: loadSegment,
            children: [
              {
                element: <Segment />,
                path: "segments/:id",
                children: [
                  {
                    action: createSegmentTargetingJob,
                    element: <SegmentTargetingJobDialog isAssociationJob />,
                    path: "associate",
                  },
                  {
                    action: createSegmentTargetingJob,
                    element: <SegmentTargetingJobDialog />,
                    path: "dissociate",
                  },
                  {
                    action: deactivateSegment,
                    element: <SegmentDeactivationDialog />,
                    path: "deactivate",
                  },
                ],
              },
              {
                action: updateSegment,
                element: <Segment isEditing />,
                path: "segments/:id/edit",
              },
            ],
          },
          {
            element: <TargetingJobs />,
            loader: loadTargetingJobs,
            path: "targeting_jobs",
          },
          {
            path: "audiences",
            loader: loadAudiences,
            element: <Audiences />,
            shouldRevalidate: ({ actionResult, defaultShouldRevalidate }) => {
              return actionResult
                ? actionResult.success
                : defaultShouldRevalidate;
            },
            children: [
              {
                path: "bulk_delete",
                action: deleteAudiences,
                element: <AudienceBulkDeleteDialog />,
              },
            ],
          },
          {
            path: "audiences/new",
            element: <NewAudience />,
            action: createNewAudience,
          },

          {
            id: "audience",
            loader: loadAudience,
            children: [
              {
                path: "audiences/:id",
                element: <Audience />,
              },
              {
                path: "audiences/:id/edit",
                action: updateAudience,
                element: <Audience isEditing />,
              },
            ],
          },
        ],
      },
      {
        id: "accountsList",
        loader: async ({ request }) => {
          const accounts = await apiClient.get("managed_plus", "/accounts", {
            signal: request.signal,
          });

          return accounts;
        },
        children: [
          {
            element: <ManagedPlusCredits />,
            path: "/managed-plus/credits",
          },
          {
            action: NewCreditAction,
            element: <NewManagedPlusCredit />,
            path: "/managed-plus/credits/new",
          },
          {
            id: "managedPlusCredit",
            loader: ManagedPlusCreditLoader,
            action: updateCreditAction,
            element: <EditManagedPlusCredit />,
            path: "/managed-plus/credits/:id/edit",
          },
          {
            action: createContactAction,
            path: "/managed-plus/credits/contacts/new",
            element: <NewManagedPlusContact />,
          },
          {
            id: "managedPlusContact",
            loader: managedPlusContactLoader,
            action: updateContactAction,
            path: "/managed-plus/credits/contacts/:id/edit",
            element: <EditManagedPlusContact />,
          },
          {
            path: "/managed-plus/credits/bulk-edit",
            action: creditBulkEditAction,
            element: <BulkEditManagedPlusCredit />,
          },
          {
            path: "/managed-plus/credits/contacts/bulk-edit",
            action: contactBulkEditAction,
            element: <BulkEditManagedPlusContact />,
          },
          {
            path: "/managed-plus/pricing/preview_floors_changes/:context",
            action: async ({ request, params }) => {
              const formData = await request.formData();

              const payload = {
                ...Object.fromEntries(formData),
                base_rate: parseFloat(formData.get("base_rate")),
              };

              return await apiClient.post(
                "managed_plus",
                `/pricing/preview_floors_changes/${params.context}`,
                payload
              );
            },
          },
          {
            shouldRevalidate: () => false,
            path: "/managed_plus/pricing/account_audience_floors/:accountId",
            action: managedplusAudienceFloorAction,
          },
          {
            path: "/managed-plus/pricing/custom-audience-floors",
            element: <ManagedPlusCustomAudienceFloors />,
            shouldRevalidate: () => false,
            handle: {
              bgcolor: () => "white",
            },
            children: [
              {
                index: true,
                shouldRevalidate: () => false,
                element: <AccountTimeOnSiteForm />,
              },
              {
                path: ":accountId",
                shouldRevalidate: () => false,
                loader: async ({ request, params }) => {
                  const { accountId } = params;
                  const searchParams = Object.fromEntries(
                    new URL(request.url).searchParams
                  );

                  return await apiClient.get(
                    "managed_plus",
                    `/pricing/account_audience_floors/${accountId}`,
                    { params: searchParams }
                  );
                },
              },
            ],
          },
        ],
      },
      {
        path: "audience-floors/global",
        element: <GlobalAudienceFloors />,
      },
      {
        element: <ManagedPlusSoxEmails />,
        path: "/managed-plus/sox-emails",
      },
      {
        element: <ManagedPlusPodcastCampaigns />,
        path: "/managed-plus/podcast-campaigns",
      },
      {
        loader: loadPodcastCampaignSettings,
        element: <ManagedPlusPodcastCampaign />,
        path: "/managed-plus/podcast-campaigns/:id",
      },
      {
        path: "publishers",
        element: <Publishers />,
      },
      {
        path: "retargeting_pixels",
        loader: loadPixels,
        element: <RetargetingPixels />,
        shouldRevalidate: ({ actionResult, defaultShouldRevalidate }) => {
          return actionResult ? actionResult.success : defaultShouldRevalidate;
        },
      },
      {
        loader: loadRetargetingPixel,
        path: "retargeting_pixels/:id/edit",
        action: updateRetargetingPixel,
        element: <RetargetingPixel isEditing />,
      },
      {
        path: "retargeting_pixels/new",
        element: <NewRetargetingPixel />,
        action: createNewRetargetingPixel,
      },
      {
        loader: loadRetargetingPixel,
        path: "retargeting_pixels/:id",
        element: <RetargetingPixel />,
      },
      {
        element: <PlacementPrebidConfigurations />,
        path: "prebid_accounts/:prebid_account_id/placement_prebid_configurations",
      },
      {
        path: "programmatic_supply",
        children: [
          {
            path: "global_settings",
            action: updateProgrammaticSupplySettings,
            loader: programmaticSupplySettingsLoader,
            element: <ProgrammaticSupplyGlobalSettings />,
          },
          {
            loader: programmaticSupplyPublishersLoader,
            path: "publishers",
            element: <ProgrammaticSupplyPublishers />,
          },
          {
            loader: programmaticSupplyPublisherLoader,
            path: "publishers/:id",
            action: updateProgrammaticSupplyPublisher,
            element: <ProgrammaticSupplyPublisher />,
          },
          {
            loader: programmaticSupplyBiddersLoader,
            id: "bidders",
            path: "bidders",
            element: <ProgrammaticSupplyBidders />,
            children: [
              {
                action: createBidder,
                path: "new",
                element: <NewBidder />,
              },
              {
                loader: bidderLoader,
                id: "bidderLoader",
                path: ":id",
                children: [
                  {
                    action: updateBidder,
                    path: "edit",
                    element: <EditBidder />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // react-router only surfaces its built-in 404 when the root route has no
        // loader, so unmatched URLs are turned into a route error here instead.
        path: "*",
        loader: ({ request }) => {
          throw json(
            `Error: No route matches URL "${new URL(request.url).pathname}"`,
            { status: 404, statusText: "Not Found" }
          );
        },
      },
    ],
  },
];

export default routes;
